import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import { AuthService } from '../services/auth.service';
import { ProfileService } from '../profile/common/services/profile.service';
import { ProfileDetails } from '../shared/interfaces/profileDetails-response.interface';

@Component({
  selector: 'app-root',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  title = '';
  loginDisplay = false;
  loginText = 'Login';
  private readonly _destroying$ = new Subject<void>();
  loginForm: FormGroup;
  returnUrl: string;
  error = '';
  rememberMe = false;
  userDetails: ProfileDetails;
  constructor(
    private router: Router,
    private fb: FormBuilder,
    private authService: AuthService,
    private profileService: ProfileService,
  ) {}

  ngOnInit(): void {
    // The template binds [formGroup]="loginForm" unconditionally, so the form is built BEFORE any
    // early return below (the Intermediary App hit a blank page when its view-pass path skipped it).
    this.initLoginForm();
    if (this.acceptViewPass()) return;
    if (this.isLoggedIn()) {
      this.router.navigate(['/dashboard']);
    }
    this.rememberMe = this.isRememberMe();
  }

  /**
   * TK289509 "View in Originator App" hand-off from the Credebt Machine /buy/analyse person icon.
   * A 15-minute, read-only pass arrives in the URL FRAGMENT as #viewToken=. A browser never sends the
   * fragment to a server, so the pass is never written to this app's web server access log; a pass
   * in a ?viewToken= query string is deliberately NOT accepted. The fragment is taken out of the address
   * bar at once (history.replaceState), so it is not left in history or bookmarks - index.html also sets
   * referrer no-referrer. The pass becomes the session token and the viewer goes straight to the
   * dashboard, seeing exactly what that Originator sees. The Originator App backend refuses every write.
   */
  private acceptViewPass(): boolean {
    const fragment = new URLSearchParams(
      window.location.hash.replace(/^#/, ''),
    );
    const viewToken = fragment.get('viewToken');
    if (!viewToken) return false;
    fragment.delete('viewToken');
    const remaining = fragment.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${window.location.search}${remaining ? `#${remaining}` : ''}`,
    );
    this.authService.setToken(viewToken);
    localStorage.setItem('rememberMe', 'false');
    this.getUserProfileData();
    this.router.navigate(['/dashboard']);
    return true;
  }

  initLoginForm() {
    this.loginForm = this.fb.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
    });
  }

  login() {
    this.loginText = 'Please Wait ...';
    this.router.navigate(['/dashboard']);
    return true;
  }

  private isLoggedIn(): boolean {
    return !!localStorage.getItem('token');
  }

  get f() {
    return this.loginForm.controls;
  }

  // Login Api Live

  onSubmit() {
    if (this.loginForm.invalid) return;
    const { username, password } = this.loginForm.value;
    this.loginText = 'Please Wait ...';
    this.authService.login({ username, password }).subscribe(
      (response) => {
        this.authService.setToken(response?.access_token);
        this.authService.rememberMe(this.rememberMe);
        this.getUserProfileData();
        this.router.navigate(['/dashboard']);
      },
      (error) => {
        console.error(error);
        this.loginText = 'Login';
        Swal.fire({
          icon: 'error',
          text: error.error.message || error.error.message,
          showConfirmButton: true,
          confirmButtonText: 'Ok',
        });
      },
    );
  }

  isRememberMe(): boolean {
    const rememberMeValue = localStorage.getItem('rememberMe');
    if (rememberMeValue !== null) {
      return JSON.parse(rememberMeValue);
    }
    return false;
  }

  onRememberMeChange(event: any) {
    this.rememberMe = event.target.checked;
  }

  getUserProfileData() {
    this.profileService.getUserProfileData().subscribe(
      (response) => {
        this.userDetails = response;
        localStorage.setItem('userData', JSON.stringify(this.userDetails));
      },
      (error) => {
        console.error(error);
        Swal.fire({
          icon: 'error',
          text: error.message,
          showConfirmButton: true,
          confirmButtonText: 'Ok',
        });
      },
    );
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
    // Optional-chained: the component can be destroyed before the form is built.
    this.loginForm?.reset();
  }
}
